<template>
  <div class="content">
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-12">
          <Card>
            <template slot="header">
              <div class="header-container">
                <div class="dropdown-container">
                  <EmployeeDropdown
                    :employees="$store.state.auth.allUsers"
                    :company="$store.state.auth.company"
                    defaultFilter="active"
                    @update="filteredUsers = $event"
                  />
                </div>
                <div class="button-container">
                  <button class="applyleave" @click="openModal">
                    Apply for leave
                  </button>
                </div>
              </div>
            </template>
            <div style="margin-bottom: 20px">
              <CardsSection :cards="cards" />
            </div>
            <div v-if="isLoading" class="loading-text">Loading data...</div>
             <CustomTable v-else :columns="tableColumns" :data="leaves" :loading="isLoading">

              <template #assignees="{ item }">
                <span class="truncate-text" :title="item.assignees">{{ item.assignees || '-' }}</span>
              </template>

              <template #employee="{ item }">
                <span class="truncate-text" :title="item.employee">{{ item.employee || '-' }}</span>
              </template>

              <template #type="{ item }">
                <span class="truncate-text" :title="item.type">{{ item.type || '-' }}</span>
              </template>

              <template #noofdays="{ item }">
                <span class="truncate-text" :title="item.noofdays">{{ item.noofdays || '-' }}</span>
              </template>

              <template #dates="{ item }">
                <span class="truncate-text" :title="item.dates">{{ item.dates || '-' }}</span>
              </template>

              <template #createdAt="{ item }">
                {{ formatRelativeTime(item.createdAt) }}
              </template>

              <template #reason="{ item }">
                <span class="truncate-text" :title="item.reason">{{ item.reason || '-' }}</span>
              </template>

              <template #leavesBalance="{ item }">
                <span class="truncate-text" :title="item.leavesBalance">{{ item.leavesBalance || '-' }}</span>
              </template>

              <template #comment="{ item }">
                <span class="truncate-text" :title="item.comment">{{ item.comment || '-' }}</span>
              </template>

              <template #status="{ item }">
                <div v-if="item.status === 'Pending' && user.uid === item.userId">
                  <button 
                    class="withdraw-btn" 
                    @click.stop="withdrawLeave(item)"
                  >
                    Withdraw
                  </button>
                </div>
                <span v-else :class="getStatusClass(item.status)">
                  {{ item.status }}
                </span>
              </template>

              <template #action="{ item }">
                <button
                  class="btn btn-sm btn-info action-button btn-leave-response"
                  :disabled="item.status === 'Auto-Deducted' || isMonthPassedAndPayrollProcessed(item) || item.status === 'Withdrawn'"
                  :class="{ 'disabled-btn': item.status === 'Auto-Deducted' || isMonthPassedAndPayrollProcessed(item) || item.status === 'Withdrawn' }"
                  @click="openActionModal(item)"
                >
                  {{ item.status === 'Approved' || item.status === 'Rejected' ? 'Update' : 'Response' }}
                </button>
              </template>

            </CustomTable>
          </Card>
        </div>
      </div>
      <ReuseableModal :modelValue="isActionModalOpen" @update:modelValue="isActionModalOpen = $event"
        :loading="loading.responseLeaves" title="Leave Action" submitButtonText="Submit" @submit="submitAction">
        <form @submit.prevent="submitAction">
          <div class="form-group">
            <label>Comment</label>
            <textarea v-model="selectedLeave.comment" class="form-control" rows="2"></textarea>
          </div>
            <div class="form-group" style="margin-left:2px">
            <label>Status<span class="required-asterisk">*</span></label>
            <div class="radio-group">
              <div class="radio-single">
                <input type="radio" id="approved" value="Approved" v-model="selectedLeave.status" name="leaveStatus" required />
                <label for="approved">Approved</label>
              </div>
              <div class="radio-single">
                <input type="radio" id="rejected" value="Rejected" v-model="selectedLeave.status" name="leaveStatus" required />
                <label for="rejected">Rejected</label>
              </div>
            </div>
          </div>
        </form>
      </ReuseableModal>
      <ReuseableModal :modelValue="isModalOpen" @update:modelValue="isModalOpen = $event" title="Add Leave" :loading="loading.applyLeave"
        @submit="addLeave" submitButtonText="Submit">
        <form @submit.prevent="addLeave">
          <div class="">
            <CustomDropdown 
              :required="true"
              id="assignee-dropdown" label="Assignees" :options="assigneeOptions" 
              :value="selectedAssigneeName" placeholder="Select Assignee" :showError="formSubmitted && 
              !newLeave.assigneeId" errorMessage="Please select an assignee" @change="handleAssigneeChange" 
            />
          </div>
          <div class="">
            <CustomDropdown  
                :required="true"
                id="leave-type-dropdown" label="Leave Type"
                :options="leaveTypeOptions" :value="newLeave.type" placeholder="Select Leave Type"
                :showError="formSubmitted && !newLeave.type" errorMessage="Please select a leave type" 
                @change="handleLeaveTypeChange" 
            />
          </div>
          <div class="form-group" style="margin-bottom: 18px;">
            <label>Duration</label>
            <div style="margin-top: 8px; display: flex; align-items: center; gap: 30px;">
              <label style="">
                <input type="radio" v-model="duration" value="full" checked> Full Day
              </label>
              <label>
                <input type="radio" v-model="duration" value="half"> Half Day
              </label>
            </div>
          </div>
          <div class="row" style="margin-bottom: 16px;">
            <div class="col-md-6">
              <label for="startDate">Start Date<span class="required-asterisk">*</span></label>
              <input type="date" id="startDate" class="form-control" v-model="newLeave.startDate" required />
            </div>
            <div class="col-md-6">
              <label for="endDate">End Date<span class="required-asterisk">*</span></label>
              <input type="date" id="endDate" class="form-control" v-model="newLeave.endDate" required />
            </div>
          </div>
          <div class="">
            <label for="reason">Reason<span class="required-asterisk">*</span></label>
            <textarea id="reason" placeholder="Type you reason..." class="form-control" v-model="newLeave.reason" required></textarea>
          </div>
        </form>
      </ReuseableModal>
      <ReuseableModal 
        :modelValue="isWithdrawModalOpen" 
        @update:modelValue="isWithdrawModalOpen = $event" 
        title="Withdraw Leave" 
        :loading="loading.withdrawLeave"
        @submit="confirmWithdraw"
        submitButtonText="Yes, Withdraw">
        <p class="m-0 p-0" >Are you sure you want to withdraw this leave? This action cannot be undone.</p>
      </ReuseableModal>

    </div>
  </div>
</template>

<script>
import CustomTable from "src/components/UIComponents/CustomTable.vue";
import Card from "../UIComponents/Cards/Card.vue";
import CardsSection from "./CardsSection.vue";
import ReuseableModal from "../UIComponents/ReuseableModal.vue"; 
import firebase from "../../assets/config/firebase";
import CustomDropdown from '../UIComponents/CustomDropdown.vue';
import moment from 'moment';
import swal from 'sweetalert';
import { calculateWorkingDays } from "src/assets/config/helpers.js"
import { 
  isHoliday, 
  calculateWorkingDaysExcludingHolidays 
} from "src/assets/config/helpers.js";
import EmployeeDropdown from "../UIComponents/EmployeeDropdown.vue"
import { createActivityPayload, logAdminActivity } from "../../utils/adminLogs";
import { handleActivityNotification } from "../../services/notificationService";

export default {
  components: { CustomTable, Card, ReuseableModal,CustomDropdown, CardsSection, EmployeeDropdown},
  data() {
    return {
      filteredUsers:[],
      cards: [],
      loading: { applyLeave: false, responseLeaves: false, withdrawLeave: false },
      isLoading: false,
      isModalOpen: false,
      formSubmitted: false,
      duration: "full",                
      user: this.$store.state.auth.user,
      leaveTypeOptions: ["Casual Leave", "Sick Leave", "Emergency Leave"], 
      newLeave: {
        startDate: "",
        endDate: "",
        assignees: "",
        assigneeId: "",
        type: "",
        noofdays: "",
        reason: "",
        status: "Pending",
      },
      userId : this.$store.state.auth.user.uid,
      tableColumns: [
        { label: "Assignees", key: "assignees", sticky: "left", sortable: true, width:150 },
        { label: "Employee", key: "employee", sticky: "left", sortable: true, width:140 },
        { label: "Type", key: "type", sortable: true },
        { label: "No Of Days", key: "noofdays", sortable: true},
        { label: "Dates", key: "dates", sortable: true },
        { label: "Applied At", key: "createdAt",  sortable: true, formatter: this.formatRelativeTime , width:180  },
        { label: "Reason", key: "reason", width:200  },
        { label: "leavesBalance", key: "leavesBalance" },
        { label: "Comments", key: "comment", width:150  },
        { label: "Status", key: "status", width:130},
        { label: "Action", key: "action" ,sticky: "right" ,width:130}, 
      ],
      isActionModalOpen: false,
      selectedLeave: {
        comment: "",
        status: "",
      },
      payrollProcessedMap: {},
      holidays: [],
      isWithdrawModalOpen: false,
      selectedLeaveToWithdraw: null,
    };
  },
  computed: {
    leaves() {
      let leaves = this.$store.state.auth.AllLeaves || [];
      const userIds = this.filteredUsers.map(user => user.uid);
      const filterLeaves = leaves.filter(leave => userIds.includes(leave.userId));
      return filterLeaves || [];
    },
    isResignedUser() {
      const currentUser = this.$store.state.auth.user;
      return currentUser.status === "Resigned";
    },
    filteredUsers() {
      const company = this.$store.state.auth.company;
      const companyUserIds = company.users.map(user => user.uid);
      const filteredAdmins = this.$store.state.auth.allUsers.filter(
        user => companyUserIds.includes(user.uid) && user.role === "admin" && user.uid !== this.userId
      );
      const emailMatchUser = this.$store.state.auth.allUsers.find(
        user => user.email === company.email &&
          user.role === "admin" && user.uid !== this.userId
      );
      return emailMatchUser ? [...filteredAdmins, emailMatchUser] : filteredAdmins;
    },
    assigneeOptions() {
      return this.filteredUsers.map(user => user.username);
    },
    selectedAssigneeName() {
      if (!this.newLeave.assigneeId) return '';
      const selectedUser = this.filteredUsers.find(user => user.uid === this.newLeave.assigneeId);
      return selectedUser ? selectedUser.username : '';
    },
  },
  watch: {
    "$store.state.auth.user": {
      handler(newUser) {
        this.user = newUser;
        this.updateCards();
      },
      immediate: true,
    },
    leaves: {
      handler: async function(newLeaves) {
        if (newLeaves.length > 0) {
          await this.processPayrollChecks();
          this.isLoading = false;
        }
      },
      immediate: true,
    },
  },
  methods: {
    getFirstWorkingDate(start, end, workingDays) {
      let current = new Date(start);
      const endDate = new Date(end);

      while (current <= endDate) {
        const dayNum = current.getDay();
        const isWeekend = dayNum === 0 || dayNum === 6;
        const isHol = isHoliday(current, this.holidays, this.user.shift);

        if (!isWeekend && !isHol) {
          return new Date(current); 
        }
        current.setDate(current.getDate() + 1);
      }
      return null; 
    },
    openModal() {
      if (this.isResignedUser) {
      swal("Error", "You can't apply. You are currently serving your notice period.","error");
      return;
    }
      this.duration = "full";                    
      this.isModalOpen = true;      
    },
    closeModal() {
      this.isModalOpen = false;
    },
    handleAssigneeChange(selectedUsername) {
      const selectedUser = this.filteredUsers.find(user => user.username === selectedUsername);
      if (selectedUser) {
        this.newLeave.assigneeId = selectedUser.uid;
        this.newLeave.assignees = selectedUser.username;
      }
    },
    handleLeaveTypeChange(selectedType) {
      this.newLeave.type = selectedType;
    },
    formatRelativeTime(timestamp) {
      return moment(timestamp).fromNow();
    },
    getStatusClass(status) {
      return (
        {
          "Auto-Deducted": "status-badge auto-deducted",
          "Pending": "status-badge pending",
          "Approved": "status-badge approved",
          "Rejected": "status-badge rejected",
          "Withdrawn": "status-badge withdrawn",
        }[status] || "status-badge"
      );
    },
    updateCards() {
    
      if (!this.user || typeof this.user.leavesAllowed === 'undefined') {
        console.log("User data not fully loaded yet");
        return;
      }
      
      const leavesAllowed = parseFloat(this.user.leavesAllowed) || 0;
      const leavesTaken = parseFloat(this.user.leavesTaken) || 0;
      const leavesAvailable = parseFloat(this.user.leavesAvailable) || 0;
      const leavesBalance = leavesAllowed - leavesTaken;
      this.cards = [
        { value: leavesAvailable.toFixed(1), label: "Total yearly leave(s)", color: "#007bff" },
        { value: leavesAllowed.toFixed(1), label: "Allowed Leave(s)", color: "#00c7c7" },
        { value: leavesTaken.toFixed(1), label: "Taken Leave(s)", color: "#ff4d4d" },
        { value: leavesBalance.toFixed(1), label: "Balance Leave(s)", color: "#00aaff" },
      ];
    },
    async fetchHolidays() {
      try {
        const holidaysData = await firebase.getAllHolidays();
        this.holidays = Array.isArray(holidaysData) ? holidaysData : [];
      } catch (error) {
        console.error("❌ Failed to fetch holidays:", error);
      }
    },
    checkHolidaysInRange(start, end) {
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (isHoliday(d, this.holidays, this.user.shift)) return true;
      }
      return false;
    },
    isRangeOnlyHolidays(start, end, workingDays) {
      let hasWorkingDay = false;
      let hasHoliday = false;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const isHol = isHoliday(d, this.holidays, this.user.shift);
        const isWork = this.isWorkingDayForCheck(d, workingDays);
        if (isHol) hasHoliday = true;
        if (!isHol && isWork) hasWorkingDay = true;
      }
      return hasHoliday && !hasWorkingDay;
    },
    isWorkingDayForCheck(date, workingDays) {
      const workingDaysNums = workingDays.map(day =>
        ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].indexOf(day)
      );
      return workingDaysNums.includes(date.getDay());
    },

    withdrawLeave(item) {
      this.selectedLeaveToWithdraw = item;
      this.isWithdrawModalOpen = true;
    },

    async confirmWithdraw() {
      if (!this.selectedLeaveToWithdraw) return;

      this.loading.withdrawLeave = true;

      try {        
        const leave = this.selectedLeaveToWithdraw;

        const range = `${leave.startDate} to ${leave.endDate || leave.startDate}`;
        const dayText = leave.noofdays === 0.5 ? 'half day' : (leave.noofdays > 1 ? 'days' : 'day');
        const title = `${this.user.username} withdrew ${leave.type} from ${range} (${leave.noofdays} ${dayText})`;
        const description = `${this.user.email} withdrew ${leave.employee}'s ${leave.type} application from ${range} (${leave.noofdays} ${dayText}).`;

        await logAdminActivity(
          createActivityPayload({
            user: this.user,
            title,
            description,
            username: leave.employee, 
            type: "leaves", 
            uid: leave.userId
          })
        );
        await firebase.updateLeaveStatusAndComment(leave.id , "Withdrawn", this.user.uid);
        leave.status = "Withdrawn";
        this.fetchLeaves();

        swal("Success", "Leave has been withdrawn successfully.", "success");
      } catch (error) {
        console.error("Error withdrawing leave:", error);
        swal("Error", "Failed to withdraw leave. Please try again.", "error");
      } finally {
        this.loading.withdrawLeave = false;
        this.isWithdrawModalOpen = false;
        this.selectedLeaveToWithdraw = null;
      }
    },

    async addLeave() {
      this.formSubmitted = true; 
      if (!this.newLeave.assigneeId || !this.newLeave.type || !this.newLeave.startDate || !this.newLeave.endDate || !this.newLeave.reason) {
        swal("Error", "Please fill all required fields.", "error");
        return;
      }

      const start = new Date(this.newLeave.startDate);
      const end = this.newLeave.endDate
        ? new Date(this.newLeave.endDate)
        : new Date(this.newLeave.startDate);

      const startYear = start.getFullYear();
      const workingDays =
        this.user.workingdays ||
        (this.$store.state.auth.constants && this.$store.state.auth.constants.days) ||
        [];
      let actualWorkingDays = calculateWorkingDays(start, end, workingDays);
      const isSingleDay = this.newLeave.startDate === this.newLeave.endDate || !this.newLeave.endDate;
      if (this.duration === "half") {
        if (!isSingleDay) {
          swal("Error", "Half day is only allowed for single day leaves.", "error");
          return;
        }
        actualWorkingDays = 0.5;
      }
      if (actualWorkingDays <= 0) {
       if (this.newLeave.startDate === this.newLeave.endDate) {
          swal("Error", "Leave cannot be applied on weekend days.", "error");
        } else {
          swal("Error", "No working days found in the selected range.", "error");
        }
        return;
      }
      if (this.holidays.length === 0) {
        await this.fetchHolidays();
      }
      const hasHolidayInRange = this.checkHolidaysInRange(start, end);
      const holidayOnly = this.isRangeOnlyHolidays(start, end, workingDays);

      if (holidayOnly) {
        swal("Error", "You cannot apply leave on holidays", "error");
        return;
      }
      if (hasHolidayInRange) {
        actualWorkingDays = calculateWorkingDaysExcludingHolidays(
          start, end, workingDays, this.holidays, this.user.shift
        );

        if (this.duration === "half") {
          actualWorkingDays = 0.5;
        }
      }
      const user = this.$store.state.auth.user;
      const employee = user.username;
      const leavesAllowed = parseFloat(user.leavesAllowed) || 0;
      const leavesTaken = parseFloat(user.leavesTaken) || 0;
      const leavesBalance = leavesAllowed - leavesTaken;
     
      if (leavesBalance < actualWorkingDays) {
        swal("Error", "You don't have sufficient leave balance.", "error");
        return;
      }
      const options = { day: 'numeric', month: 'short' };
      let dates;
      if (actualWorkingDays === 0.5 || actualWorkingDays === 1) {
        const firstWorking = this.getFirstWorkingDate(start, end, workingDays);
        const displayDate = firstWorking || start;
        dates = displayDate.toLocaleDateString('en-US', options);
      } else {
        const formattedStartDate = start.toLocaleDateString('en-US', options);
        const formattedEndDate = end.toLocaleDateString('en-US', options);
        dates = `${formattedStartDate} - ${formattedEndDate}`;
      }
      const assignee = this.$store.state.auth.allUsers.find(user => user.uid === this.newLeave.assigneeId);
      if (!assignee) {
        swal("(Error)", "Selected assignee not found.", "error");
        return;
      }

      try {
        this.loading.applyLeave = true;
        
        const payload = {
          assignees: assignee.username,
          assigneeId: this.newLeave.assigneeId,
          type: this.newLeave.type,
          noofdays: actualWorkingDays,
          reason: this.newLeave.reason,
          status: "Pending",
          userId: user.uid,                   
          employee: user.username,
          dates,
          leavesBalance,
          createdAt: Date.now(),
          createdBy: "Admin",
          startDate: this.newLeave.startDate,
          endDate: this.newLeave.endDate || this.newLeave.startDate,
          duration: this.duration
        };
        const leaveId = await firebase.submitLeave("leaves", payload);
        const { type, startDate, endDate } = this.newLeave;
        
        let range = actualWorkingDays > 1 ? `from ${this.formatDate(startDate)} to ${this.formatDate(endDate ? endDate : startDate)}` : `for ${this.formatDate(startDate)}`;
        const { uid, username, email } = assignee;
        const { reason } = payload;
        
        let dayText = actualWorkingDays === 0.5 ? 'half day' : (actualWorkingDays > 1 ? 'days' : 'day');
        let title = `${this.user.username} applied ${type} ${range} (${actualWorkingDays} ${dayText})`;
        let description = `${this.user.email} applied for ${type} ${range} (${actualWorkingDays} ${dayText}).`;

        await logAdminActivity(
          createActivityPayload({
            user: this.user,
            title,
            description,
            reason,
            username, 
            type: "leaves", 
            uid
          })
        );
        await handleActivityNotification(
          {
            type: "leaves",
            activityDescription: description,
            activityName: title,
            activityOnUser: uid,              
            activityOnUserName: username,     
            activityByUserName: this.user.username,
          },
          this.$store.state.auth,
          this.user.uid
        );
        // const leaveWithId = { ...payload, id: leaveId };
        // const updatedLeaves = [...this.leaves, leaveWithId];
        // this.$store.commit("setAllLeaves", updatedLeaves);
        this.fetchLeaves();
        this.newLeave = {
          startDate: "",
          endDate: "",
          assigneeId: "",
          type: "",
          reason: "",
        };
        this.selectedAssigneeName = "";
        this.formSubmitted = false;
        this.closeModal();
        
        swal("Success", "Leave application submitted successfully!", "success");
      } catch (error) {
        console.error("Error adding leave:", error);
        swal("Error", "Failed to submit leave application. Please try again.", "error");
      } finally {
        this.loading.applyLeave = false;
      }
    },
    async fetchLeaves() {
      try {
        this.isLoading = true;
        const allLeaves = this.$store.state.auth.AllLeaves || []
        this.$store.commit("setAllLeaves", allLeaves);
        this.isLoading = false;
      } catch (error) {
        console.error("Error fetching leaves:", error);
      } finally {
        this.isLoading = false;
      }
    },
    openActionModal(leave) {
      this.selectedLeave = leave ? { ...leave } : { comment: "", status: "" };
      this.isActionModalOpen = true;
    },
    closeActionModal() {
      this.isActionModalOpen = false;
    },
    async submitAction() {
      this.loading.responseLeaves = true;
      if (this.selectedLeave && this.selectedLeave.id) {
        const { id, status, comment, userId, noofdays, createdBy, startDate, endDate, type } = this.selectedLeave;
        if (userId === this.userId) {
          swal("Error", "You cannot approve or reject your own leave.", "error");
          this.loading.responseLeaves = false;
          this.isActionModalOpen = false;
          return;
        }
        if(status=="Pending"){
           swal("Error", "Status is required", "error");
           this.loading.responseLeaves = false;
          return;
        }
        try {
          const leavesuser = await firebase.getSingleUser(userId);
          if (leavesuser) {
            const originalLeave = this.leaves.find((x) => id === x.id);
            if (originalLeave) {
              const today = new Date();
              const leaveEndDate = new Date(originalLeave.endDate);
              // if (leaveEndDate < today.setHours(0, 0, 0, 0)) {
              //   swal("Error", "You cannot take action on leaves that have already ended.", "error");
              //   return;
              // }
              if (originalLeave.status === "Approved" && status === "Rejected") {
                leavesuser.leavesTaken = (Number(leavesuser.leavesTaken) - Number(noofdays)).toFixed(1);
              } else if (originalLeave.status !== "Approved" && status === "Approved") {
                leavesuser.leavesTaken = (Number(leavesuser.leavesTaken) + Number(noofdays)).toFixed(1);
              }
            }
            const safeComment = comment || "";
            const response = await firebase.updateLeaveStatusAndComment(
              id,
              status,
              userId,
              safeComment,
              leavesuser.leavesTaken,
            );
            const { uid, username, email } = leavesuser;
            let title = "";
            let description = "";
            let dayText = noofdays === 0.5 ? 'half day' : (noofdays > 1 ? 'days' : 'day');
            let range = noofdays > 1 ? `from ${this.formatDate(startDate)} to ${this.formatDate(endDate ? endDate : startDate)}` : `for ${this.formatDate(startDate)}`;

            if (status === "Approved") {
              title = `${this.user.username} approved ${username}'s ${type} request ${range} (${noofdays} ${dayText})`
              
              description = `${this.user.email} approved ${email}'s ${type} request from ${startDate} to ${endDate ? endDate : startDate} (${noofdays} ${dayText}).`
              
              await logAdminActivity(
                createActivityPayload({
                  user: this.user,
                  title,
                  description,
                  reason: safeComment,
                  username, 
                  type: "leaves", 
                  uid
                })
              );
              await handleActivityNotification(
                {
                  type: "leaves",
                  activityDescription: description,
                  activityName: title,
                  activityOnUser: uid,               
                  activityOnUserName: username,      
                  activityByUserName: this.user.username,
                },
                this.$store.state.auth,
                this.user.uid
              );
            } else if (status === "Rejected") {
              title = `${this.user.username} rejected ${username}'s ${type} request ${range} (${noofdays} ${dayText})`

              description = `${this.user.email} rejected ${email}'s ${type} request from ${startDate} to ${endDate ? endDate : startDate} (${noofdays} ${dayText}).`

              await logAdminActivity(
                createActivityPayload({
                  user: this.user,
                  title,
                  description,
                  reason: safeComment,
                  username, 
                  type: "leaves", 
                  uid
                })
              );
              await handleActivityNotification(
                {
                  type: "leaves",
                  activityDescription: description,
                  activityName: title,
                  activityOnUser: uid,               
                  activityOnUserName: username,     
                  activityByUserName: this.user.username,
                },
                this.$store.state.auth,
                this.user.uid
              );
            }
          //  const userdata= await firebase.getSingleUser(userId)
          //  this.$store.commit("setUser", {
          //   uid: userId,
          //   ...userdata,
          //});


            if (originalLeave) {
              originalLeave.status = status;
              originalLeave.comment = safeComment;
              this.$store.commit("setAllLeaves", this.leaves);
            }
            this.loading.responseLeaves = false;
            swal("Success", "Leave status updated successfully!", "success");
            this.isActionModalOpen = false;
          } else {
            this.loading.responseLeaves = false;
             swal("Error", "User associated with this leave not found.", "error");
            console.error("User not found:", userId);
          }
        } catch (error) {
          this.loading.responseLeaves = false;
           swal("Error", "Failed to update leave status. Please try again.", "error");
          console.error("Error updating leave:", error);
        }
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    isMonthPassedAndPayrollProcessed(item) {
      const endDate = new Date(item.endDate);
      const currentDate = new Date();

      const monthPassed =
        endDate.getFullYear() < currentDate.getFullYear() ||
        (endDate.getFullYear() === currentDate.getFullYear() &&
          endDate.getMonth() < currentDate.getMonth());

      if (!monthPassed) return false;

      return this.payrollProcessedMap[item.id] || false;
    },

    async processPayrollChecks() {
      this.payrollProcessedMap = {};

      for (const leave of this.leaves) {
        const endDate = new Date(leave.endDate);
        const currentDate = new Date();

        const monthPassed =
          endDate.getFullYear() < currentDate.getFullYear() ||
          (endDate.getFullYear() === currentDate.getFullYear() &&
            endDate.getMonth() < currentDate.getMonth());
        if (!monthPassed) {
          this.$set(this.payrollProcessedMap, leave.id, false);
          continue;
        }

        const year = endDate.getFullYear();
        const month = moment(endDate).format("MMMM");
        const processed = await firebase.isPayrollProcessed(
          year,
          month,
          leave.userId
        );
        this.$set(this.payrollProcessedMap, leave.id, processed);
      }
    }
  },
  async mounted() {
    this.fetchLeaves();
    await this.fetchHolidays();
    this.$nextTick(() => {
      if (this.user) {
        this.updateCards();
      }
    });
  }
};
</script>

<style scoped>
.header-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  align-items: stretch;
  width: 100%;
  align-items:start;
  justify-content: start;
height: 130px;
}

@media (min-width: 768px) {
  .header-container {
    flex-direction: row;
    justify-content: space-between;
    align-items: start;
height: 60px;
  }
}

.dropdown-container {
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;
  min-width: 0; 
}
.required-asterisk {
  color: red;
  margin-left: 4px;
}
</style>




// WEBPACK FOOTER //
// src/components/LeaveManagement/AdminLeaveManagement.vue